import { resolveStatisticsYearLineColors } from '../../common/lib/statisticsChartPalette';
import {
	calcPrimaryPhaseProfitRatePercent,
	formatPrimaryPhaseMetricValue,
	formatPrimaryPhasePercentValue,
} from '../../charts/pipeline/lib/orderPrimaryPhaseMetric';
import { getStatisticsLensData } from '../../charts/pipeline/lib/orderPrimaryPhaseConfig';
import moment from 'moment';

const MONTH_LABELS = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
const QUARTER_LABELS = ['Q1', 'Q2', 'Q3', 'Q4'];

export const PERIOD_YOY_GRANULARITY = {
	MONTH: 'month',
	QUARTER: 'quarter',
};

export const PERIOD_YOY_VALUE_FIELD = {
	COUNT: 'count',
	TOTAL_PRICE: 'totalPrice',
	PROFIT_RATE: 'profitRate',
	RECEIVABLE: 'receivableTotal',
	QUASI_RECEIVABLE: 'quasiReceivableTotal',
	PENDING_PREPAYMENT: 'pendingPrepaymentTotal',
	PAID_PREPAYMENT: 'paidPrepaymentTotal',
};

const GRID_KEY_BY_GRANULARITY = {
	[PERIOD_YOY_GRANULARITY.MONTH]: 'byMonth',
	[PERIOD_YOY_GRANULARITY.QUARTER]: 'byQuarter',
};

const AXIS_LABELS_BY_GRANULARITY = {
	[PERIOD_YOY_GRANULARITY.MONTH]: MONTH_LABELS,
	[PERIOD_YOY_GRANULARITY.QUARTER]: QUARTER_LABELS,
};

const resolvePeriodYoyScope = (periodYoy, selectedPhase) => {
	const lensData = getStatisticsLensData(periodYoy);
	if (!lensData) return null;
	if (selectedPhase) return lensData.phases?.[selectedPhase] ?? null;
	return lensData.overview ?? null;
};

export const resolvePeriodYoyGrid = (periodYoy, selectedPhase, granularity = PERIOD_YOY_GRANULARITY.MONTH) => {
	const scope = resolvePeriodYoyScope(periodYoy, selectedPhase);
	if (!scope) return null;
	return scope[GRID_KEY_BY_GRANULARITY[granularity]] ?? null;
};

const formatAxisAmount = (value) => {
	const num = Number(value) || 0;
	if (Math.abs(num) >= 10000) return `${(num / 10000).toFixed(num >= 100000 ? 0 : 1)}万`;
	return String(num);
};

const PERIOD_VALUE_FIELD_TO_CHART_METRIC = {
	[PERIOD_YOY_VALUE_FIELD.COUNT]: PERIOD_YOY_VALUE_FIELD.COUNT,
	[PERIOD_YOY_VALUE_FIELD.TOTAL_PRICE]: 'totalPrice',
	[PERIOD_YOY_VALUE_FIELD.PROFIT_RATE]: 'profitRate',
	[PERIOD_YOY_VALUE_FIELD.RECEIVABLE]: 'receivable',
	[PERIOD_YOY_VALUE_FIELD.QUASI_RECEIVABLE]: 'quasiReceivable',
	[PERIOD_YOY_VALUE_FIELD.PENDING_PREPAYMENT]: 'pendingPrepayment',
	[PERIOD_YOY_VALUE_FIELD.PAID_PREPAYMENT]: 'paidPrepayment',
};

const resolvePeriodYoyCellValue = (cell, valueField = PERIOD_YOY_VALUE_FIELD.COUNT) => {
	if (valueField === PERIOD_YOY_VALUE_FIELD.PROFIT_RATE) {
		return calcPrimaryPhaseProfitRatePercent(cell?.trueProfitTotal, cell?.totalPrice);
	}
	return Number(cell?.[valueField]) || 0;
};

export const formatPeriodYoyValue = (value, valueField = PERIOD_YOY_VALUE_FIELD.COUNT) => {
	if (value == null) return '—';
	const chartMetric = PERIOD_VALUE_FIELD_TO_CHART_METRIC[valueField] ?? PERIOD_YOY_VALUE_FIELD.COUNT;
	return formatPrimaryPhaseMetricValue(value, chartMetric);
};

/** 自然月/季首日尚未到来（不含当月当季） */
export const isFutureCalendarPeriodIndex = (year, periodIndex, granularity = PERIOD_YOY_GRANULARITY.MONTH) => {
	const yearNum = Number(year);
	const index = Number(periodIndex);
	if (!Number.isFinite(yearNum) || !Number.isFinite(index) || index < 0) return false;

	const today = moment().startOf('day');
	const startMonth = granularity === PERIOD_YOY_GRANULARITY.QUARTER ? index * 3 : index;
	const periodStart = moment({ year: yearNum, month: startMonth, day: 1 }).startOf('day');
	return periodStart.isAfter(today);
};

/** 区间起点在未来：折线对比时间轴各点用 */
export const isFuturePeriodStartDate = (startDate) => {
	const start = moment(startDate).startOf('day');
	if (!start.isValid()) return false;
	return start.isAfter(moment().startOf('day'));
};

export const maskFutureCalendarPeriodValues = ({ year, periodIndices = null, values = [], granularity = PERIOD_YOY_GRANULARITY.MONTH }) => {
	if (!Array.isArray(values) || !values.length) return values;
	const indices =
		Array.isArray(periodIndices) && periodIndices.length === values.length ? periodIndices : values.map((_, index) => index);
	return values.map((value, index) => (isFutureCalendarPeriodIndex(year, indices[index], granularity) ? null : value));
};

/** 折线点：未来区间不描点、不连线 */
export const resolveTrendChartPeriodValue = (startDate, rawValue) => {
	if (isFuturePeriodStartDate(startDate)) return null;
	const num = Number(rawValue);
	return Number.isFinite(num) ? num : 0;
};

/** 折线 year-months 横轴子集：优先已应用筛选口径，避免 refetch 期间 API 回传旧月份 */
export const resolveLineComparePeriodSubsetIndices = (specIndices, apiIndices, maxPeriods) => {
	if (Array.isArray(specIndices) && specIndices.length > 0 && specIndices.length < maxPeriods) {
		return specIndices;
	}
	if (Array.isArray(apiIndices) && apiIndices.length > 0 && apiIndices.length < maxPeriods) {
		return apiIndices;
	}
	return null;
};

/** 构建 ECharts 多折线：每年一条线 */
export const buildPeriodYoyLineSeries = ({
	periodYoy,
	selectedPhase,
	granularity = PERIOD_YOY_GRANULARITY.MONTH,
	valueField = PERIOD_YOY_VALUE_FIELD.COUNT,
	monthIndices = null,
	periodIndices = null,
	isLight = false,
}) => {
	const yearLineColors = resolveStatisticsYearLineColors(isLight);
	const allPeriodLabels = AXIS_LABELS_BY_GRANULARITY[granularity] ?? MONTH_LABELS;
	const maxPeriods = granularity === PERIOD_YOY_GRANULARITY.QUARTER ? 4 : 12;
	const resolvedIndices = Array.isArray(periodIndices) && periodIndices.length ? periodIndices : monthIndices;
	const subsetIndices =
		Array.isArray(resolvedIndices) && resolvedIndices.length > 0 && resolvedIndices.length < maxPeriods ? resolvedIndices : null;
	const axisLabels = subsetIndices
		? subsetIndices.map((index) => allPeriodLabels[index] ?? `${index + 1}`)
		: allPeriodLabels;

	if (!periodYoy?.years?.length) {
		return { axisLabels, series: [], currencyMetric: false, percentMetric: valueField === PERIOD_YOY_VALUE_FIELD.PROFIT_RATE };
	}

	const grid = resolvePeriodYoyGrid(periodYoy, selectedPhase, granularity);
	if (!grid) {
		return { axisLabels, series: [], currencyMetric: false, percentMetric: valueField === PERIOD_YOY_VALUE_FIELD.PROFIT_RATE };
	}

	const series = periodYoy.years.map((year, index) => {
		const cells = grid[year] ?? [];
		const periodIndices = subsetIndices ?? cells.map((_, periodIndex) => periodIndex);
		const values = subsetIndices
			? subsetIndices.map((periodIndex) => resolvePeriodYoyCellValue(cells[periodIndex], valueField))
			: cells.map((cell) => resolvePeriodYoyCellValue(cell, valueField));
		return {
			year,
			key: `yoy-${granularity}-${valueField}-${year}`,
			color: yearLineColors[index % yearLineColors.length],
			values: maskFutureCalendarPeriodValues({ year, periodIndices, values, granularity }),
		};
	});

	const isPercent = valueField === PERIOD_YOY_VALUE_FIELD.PROFIT_RATE;
	const isCurrency = valueField !== PERIOD_YOY_VALUE_FIELD.COUNT && !isPercent;

	return {
		anchorYear: periodYoy.anchorYear,
		axisLabels,
		series,
		currencyMetric: isCurrency,
		percentMetric: isPercent,
		formatAxisValue: isPercent ? formatPrimaryPhasePercentValue : isCurrency ? formatAxisAmount : undefined,
	};
};

const filterPeriodYoyScopeByYears = (scope, allowedYears) => {
	if (!scope || !allowedYears?.length) return scope;
	const allowed = new Set(allowedYears);
	const pickGrid = (grid) => {
		if (!grid) return grid;
		const next = {};
		for (const [yearKey, cells] of Object.entries(grid)) {
			const year = Number(yearKey);
			if (allowed.has(year)) next[yearKey] = cells;
		}
		return next;
	};
	return {
		byMonth: pickGrid(scope.byMonth),
		byQuarter: pickGrid(scope.byQuarter),
	};
};

/** 按当前已应用年份裁剪折线 YoY 数据，避免 refetch 期间展示多余年份 */
export const filterLineComparePeriodYoyByYears = (payload, allowedYears) => {
	if (!payload?.years?.length || !allowedYears?.length) return payload;

	const allowed = [...new Set(allowedYears.map((year) => Number(year)).filter((year) => Number.isFinite(year)))].sort((a, b) => a - b);
	if (!allowed.length) return payload;
	if (payload.years.length === allowed.length && payload.years.every((year, index) => year === allowed[index])) {
		return payload;
	}

	const allowedSet = new Set(allowed);
	const years = payload.years.filter((year) => allowedSet.has(year));
	const lenses = {};
	for (const [lens, lensData] of Object.entries(payload.lenses ?? {})) {
		lenses[lens] = {
			overview: filterPeriodYoyScopeByYears(lensData.overview, years),
			phases: Object.fromEntries(
				Object.entries(lensData.phases ?? {}).map(([phase, scope]) => [phase, filterPeriodYoyScopeByYears(scope, years)]),
			),
		};
	}

	return {
		...payload,
		years: allowed,
		anchorYear: allowedSet.has(payload.anchorYear) ? payload.anchorYear : allowed[allowed.length - 1],
		lenses,
	};
};

/** 兼容旧月度结构（overview[year] 直挂数组） */
export const normalizePeriodYoyPayload = (payload) => {
	if (!payload?.lenses) return payload;
	const sampleOverview = Object.values(payload.lenses)[0]?.overview;
	if (sampleOverview?.byMonth) return payload;

	const lenses = {};
	for (const [lens, lensData] of Object.entries(payload.lenses)) {
		lenses[lens] = {
			overview: { byMonth: lensData.overview, byQuarter: {} },
			phases: Object.fromEntries(
				Object.entries(lensData.phases ?? {}).map(([phase, grid]) => [phase, { byMonth: grid, byQuarter: {} }]),
			),
		};
	}
	return { ...payload, lenses };
};
